import React, { useEffect, useRef, useState } from "react";
import PageInfo from "../UI/PageInfo";
import { useSetRecoilState } from "recoil";

import width from "./helper";
import tableState from "../Atoms/Table/state";
import util from "../../../global/utils/Helpers";

const numberParsers = [
  "positiveNumber",
  "wholeNumber",
  "positiveWholeNumber",
  "positiveWholeNumberToInt",
];

const BaseInput = ({
  input,
  meta,
  schemaProps,
  hidden,
  inputHidden,
  maxLength,
  disabled,
  inputType,
  value,
  formApi,
  ...rest
}) => {
  if (schemaProps && schemaProps.value) {
    let _val = schemaProps.value;
    input.onChange(input.value);
    formApi.change(schemaProps.machine_name, _val);
  }
  const { clearSchema, addTableError, parent_machine_name, clearSchemaIfNA } =
    schemaProps;

  // For trimming whitespace on unmounting component
  const [isFocused, setIsFocused] = useState(false);
  const isUnmounting = useRef(false);

  // Pt 1: Mark the component unmounting with empty dependency array to trigger cleanup below
  useEffect(() => {
    return () => {
      isUnmounting.current = true;
    };
  }, []);

  // Pt 2: Trim whitespace on last focused field when component is umounting
  useEffect(() => {
    return () => {
      if (
        isFocused &&
        isUnmounting.current &&
        !numberParsers.includes(schemaProps.parse)
      ) {
        handleOnBlur();
      }
    };
  }, [input?.value, isFocused]);

  const handleOnBlur = () => {
    setIsFocused(false);
    if (!numberParsers.includes(schemaProps.parse)) {
      // Prevents onBlur higher up the tree from being overwritten, otherwise validation errors stop showing
      input.onBlur(input.onChange(util.removeBadChars(input.value).trim()));
    } else {
      input.onBlur(() => {
        input.onChange(input.value);
      });
    }
  };

  let placeholder = null;
  if (schemaProps.parse) {
    switch (schemaProps.parse) {
      case "fein":
        placeholder = "##-#######";
        break;
      case "phone":
        // could check country here and update placeholder
        placeholder = "(333) 333-4444";
        break;
      case "monthYear":
        placeholder = "mm/yyyy";
        break;
      default:
    }
  }
  const setErrors = useSetRecoilState(tableState);
  const setTableErrors = (val, error, machineName, rowIndex, columnId) => {
    let newTableErrors;
    if (error) {
      setErrors((errors) => {
        newTableErrors = JSON.parse(JSON.stringify(errors));
        !(machineName in newTableErrors) &&
          (newTableErrors[`${machineName}`] = {});
        // rowKey exists? if not create it
        !(rowIndex in newTableErrors[`${machineName}`]) &&
          (newTableErrors[`${machineName}`][`${rowIndex}`] = []);
        // item in array exists? if not push it
        !newTableErrors[`${machineName}`][`${rowIndex}`].includes(columnId) &&
          newTableErrors[`${machineName}`][`${rowIndex}`].push(columnId);
        return newTableErrors;
      });
    }
  };
  const clearFields = () => {
    clearSchema.forEach((c) => {
      // clearing field on fieldArray
      if (c.includes(".")) {
        const [fieldArray, fieldArrayField] = c.split(".");
        const formValues = formApi.getState().values;
        if (
          formValues &&
          fieldArray &&
          Array.isArray(formValues[fieldArray]) &&
          formValues[fieldArray].length
        ) {
          const fieldArrayRows = formValues[fieldArray];
          fieldArrayRows.forEach((row, index) => {
            const fieldToClear = `${fieldArray}[${index}].${fieldArrayField}`;
            formApi.change(fieldToClear, null);
          });
        }
      }
      // clearing simple field
      else {
        let fieldToClear = parent_machine_name
          ? `${parent_machine_name}[0]${c}`
          : c;
        formApi.change(fieldToClear, null);
      }
    });
  };

  const clearFieldsIfNA = (valueToCompare) => {
    const na_regex = /^[Nn]\/[Aa]$/;
    if (valueToCompare?.match(na_regex)) {
      clearSchemaIfNA.forEach((c) => {
        // clearing field on fieldArray
        if (c.includes(".")) {
          const [fieldArray, fieldArrayField] = c.split(".");
          const formValues = formApi.getState().values;
          if (
            formValues &&
            fieldArray &&
            Array.isArray(formValues[fieldArray]) &&
            formValues[fieldArray].length
          ) {
            const fieldArrayRows = formValues[fieldArray];
            fieldArrayRows.forEach((row, index) => {
              const fieldToClear = `${fieldArray}[${index}].${fieldArrayField}`;
              formApi.change(fieldToClear, null);
            });
          }
        }
        // clearing simple field
        else {
          let fieldToClear = parent_machine_name
            ? `${parent_machine_name}[0]${c}`
            : c;
          formApi.change(fieldToClear, null);
        }
      });
    }
  };

  const addTableFieldError = () => {
    addTableError.forEach((c) => {
      const [fieldArray, fieldArrayField] = c.split(".");
      const formValues = formApi.getState().values;
      const fieldArrayRows = formValues[fieldArray];
      if (fieldArrayRows) {
        fieldArrayRows.forEach((_field, index) => {
          setTableErrors(
            null,
            "This field is required",
            fieldArray,
            index,
            fieldArrayField
          );
        });
      }
    });
  };

  return (
    <>
      <div>
        {schemaProps.info && schemaProps.info.position === "top" && (
          <PageInfo {...schemaProps.info} />
        )}
      </div>
      <div>
        {
          // A way to hide the input and assign any value to the field without user input
          !hidden && !inputHidden && (
            <input
              disabled={disabled || false}
              id={input.name}
              title={schemaProps.code}
              className={
                disabled
                  ? `usa-input usa-input-disabled`
                  : `usa-input grid-col-${width(schemaProps)}`
              }
              maxLength={maxLength}
              placeholder={placeholder}
              {...input}
              onChange={(e) => {
                clearSchema && clearFields();
                clearSchemaIfNA && clearFieldsIfNA(e.target.value);
                addTableError && addTableFieldError();
                input.onChange(util.removeBadChars(e.target.value));
              }}
              // onBlur is for removing leading and trailing whitespace when user leaves field
              onBlur={handleOnBlur}
              onFocus={() => setIsFocused(true)}
              type={inputType || null}
            />
          )
        }
      </div>
      <div>
        {schemaProps.info && schemaProps.info.position === undefined && (
          <PageInfo {...schemaProps.info} />
        )}
      </div>
    </>
  );
};

export default BaseInput;
